import React, { useMemo, useState } from 'react';
import styles from './BonificiTable.module.scss';
import CustomTable from '../../../Common/CustomTable';
import { ReactComponent as EyeIcon } from '../../../../assets/svg/eye_gray.svg';
import IconButton from '../../../Common/Buttons/IconButton';
import clx from 'classnames';
import dayjs from 'dayjs';
import Modal from 'Containers/Common/Modal';
import PaymentTable from '../PaymentTable';
import { useTranslation } from 'react-i18next';
import { Bot, ChevronRight } from 'lucide-react';

const mapTransactions = (transazioni = []) =>
  transazioni.map((t) => ({
    ...t,
    clientName: `${t.cliente?.nome ?? ''} ${t.cliente?.cognome ?? ''}`.trim(),
    totalePagato: t?.importo,
    voucher: t?.voucher ?? 0,
    disableRimborsa: false,
  }));

const transferPeriod = (original) => {
  const fromDate = original.transazioni?.length > 0 ? original.transazioni[0].created_at : null;
  const toDate =
    original.transazioni?.length > 0
      ? original.transazioni[original.transazioni.length - 1].created_at
      : dayjs(original.createdAt).subtract(1, 'day');
  return {
    from: fromDate ? dayjs(fromDate).format('DD/MM/YYYY') : null,
    to: dayjs(toDate).format('DD/MM/YYYY'),
  };
};

export default ({ values, isMobile, entityId }) => {
  const { t } = useTranslation();
  const data = useMemo(() => values || [], [values]);
  const [modalValue, setModalValue] = useState({ isOpen: false, data: [] });

  const openDetails = (original) => {
    setModalValue({ isOpen: true, data: mapTransactions(original?.transazioni) });
  };

  const columns = useMemo(
    () =>
      isMobile
        ? [
            {
              Header: '',
              accessor: 'actions',
              Cell: ({ row: { original } }) => {
                const period = transferPeriod(original);
                return (
                  <button
                    type="button"
                    className={styles.bonifico_mobile_card}
                    aria-label={t('payments.bonifici.detailTransactions')}
                    onClick={() => openDetails(original)}
                  >
                    <div className={styles.mobile_copy}>
                      <span className={styles.payment_name}>
                        {dayjs(original.data).format('DD/MM/YYYY')}
                      </span>
                      <span className={styles.payment_meta}>
                        {period.from
                          ? `${period.from} → ${period.to}`
                          : t('payments.table.notAvailable')}
                      </span>
                    </div>
                    {!!original?.generatedAutomatically && (
                      <span className={styles.auto_badge} title={t('payments.bonifici.automatic')}>
                        <Bot size={14} strokeWidth={2.2} />
                      </span>
                    )}
                    <span className={styles.amount_pill}>+€{(original.totale).toFixed(2)}</span>
                    <ChevronRight size={18} strokeWidth={2.2} />
                  </button>
                );
              },
            },
          ]
        : [
            {
              Header: t('payments.bonifici.headers.paymentDate'),
              accessor: 'created_at',
              Cell: ({ row: { original } }) => {
                return (
                  <div className={styles.payment}>
                    <span className={styles.payment_date}>
                      {dayjs(original.data).format('DD/MM/YYYY')}
                    </span>
                  </div>
                );
              },
            },
            {
              Header: t('payments.bonifici.headers.totalCredited'),
              accessor: 'totaleAccreditato',
              Cell: ({ row: { original } }) => {
                return (
                  <div className={styles.payment_value}>€{(original.totale).toFixed(2)}</div>
                );
              },
            },
            {
              Header: t('payments.bonifici.headers.from'),
              accessor: 'pagamentiDal',
              Cell: ({ row: { original } }) => {
                const { from } = transferPeriod(original);
                return (
                  <div className={styles.payment_date}>
                    {from ?? t('payments.table.notAvailable')}
                  </div>
                );
              },
            },
            {
              Header: t('payments.bonifici.headers.to'),
              accessor: 'pagamentiAl',
              Cell: ({ row: { original } }) => {
                const { to } = transferPeriod(original);
                return <div className={styles.payment_date}>{to}</div>;
              },
            },
            {
              Header: '',
              accessor: '*',
              Cell: ({ row: { original } }) => {
                return (
                  <div className={styles.row_actions}>
                    {!!original?.generatedAutomatically && (
                      <span className={styles.auto_badge} title={t('payments.bonifici.automatic')}>
                        <Bot size={16} strokeWidth={2.2} />
                      </span>
                    )}
                    <IconButton
                      onClick={() => openDetails(original)}
                      svg={<EyeIcon />}
                      type={'default'}
                      size={'32px'}
                    />
                  </div>
                );
              },
            },
          ],
    [isMobile, t],
  );

  return (
    <>
      <div
        className={clx(styles.table_card, {
          [styles.table_card_mobile]: isMobile,
        })}
      >
        <CustomTable
          data={data}
          columns={columns}
          serverSidePagination={false}
          isMobile={isMobile}
          pageRows={20}
          entityId={entityId || 'bonifici'}
        />
      </div>
      <Modal
        title={t('payments.bonifici.detailTransactions')}
        setIsModalOpen={() => setModalValue({ isOpen: false, data: null })}
        isModalOpen={modalValue.isOpen}
        width={isMobile ? '100%' : '1103px'}
        showCloseBtn={true}
      >
        <PaymentTable
          serverSidePagination={false}
          pageCount={Math.ceil(modalValue?.data?.length / 5)}
          total={Math.ceil(modalValue?.data?.length)}
          values={modalValue?.data}
          isMobile={isMobile}
          pageRows={5}
        />
      </Modal>
    </>
  );
};
